
import { t } from '../../../lib/i18n';
import { lotSiteHref } from '../../bids/utils';
import { type AuctionScheduleItem } from '../types';
import { UKRAINE_TIMEZONE, formatCurrency, getBidManagementUrl, getEffectiveEndDate } from '../utils';

/**
 * Status palette. The artboard colours this column by FEED health — a field
 * `GET /admin/auction-schedule` does not project — so it carries the bidding
 * status instead, in the tokens the rest of Design D already assigns to those
 * three meanings.
 */
const BADGE: Record<AuctionScheduleItem['status'], string> = {
  active: 'bg-cf-success-tint text-cf-success',
  ending: 'bg-cf-danger-tint text-cf-danger',
  ended: 'bg-cf-fill text-cf-ink-subtle',
};
const DOT: Record<AuctionScheduleItem['status'], string> = {
  active: 'bg-cf-accent',
  ending: 'bg-cf-danger',
  ended: 'bg-cf-past',
};

const DATE_FMT = new Intl.DateTimeFormat('uk-UA', {
  timeZone: UKRAINE_TIMEZONE,
  day: '2-digit',
  month: '2-digit',
});
const TIME_FMT = new Intl.DateTimeFormat('uk-UA', {
  timeZone: UKRAINE_TIMEZONE,
  hour: '2-digit',
  minute: '2-digit',
});

function safeFormat(formatter: Intl.DateTimeFormat, value: string): string {
  const date = getEffectiveEndDate(value);
  return Number.isNaN(date.getTime()) ? '—' : formatter.format(new Date(value));
}

/** The lot's card photo (owner, 2026-10-13: «додай зображення лоту»); a grey tile when it has none. */
function LotPhoto({ src }: { src: string | null | undefined }) {
  return src ? (
    <img
      src={src}
      alt=""
      loading="lazy"
      data-testid="schedule-lot-photo"
      className="h-10 w-[60px] shrink-0 rounded-cf-sm bg-cf-fill object-cover"
    />
  ) : (
    <span aria-hidden className="h-10 w-[60px] shrink-0 rounded-cf-sm bg-cf-fill" />
  );
}

/**
 * One closing. Desktop is the artboard's grid row, mobile its stacked card with
 * the 3px status stripe down the left edge.
 */
export function ListRow({ auction }: { auction: AuctionScheduleItem }) {
  const date = safeFormat(DATE_FMT, auction.end_date);
  const time = safeFormat(TIME_FMT, auction.end_date);
  const bidCount = auction.bidCount ?? 0;
  const bid = auction.highestBidAmount
    ? formatCurrency(auction.highestBidAmount)
    : t('auctionSchedule.board.none');
  const statusLabel = t(`auctionSchedule.filters.status.${auction.status}`);
  const href = getBidManagementUrl(auction);
  // The row opens the car on the auction site in a new tab (owner, 2026-10-18 —
  // the admin lot page is retired); «Редагувати» keeps its own target above
  // the row link.
  const lotHref = lotSiteHref({ carId: auction._id, listingId: auction.listing_id });

  return (
    <>
      {/* ── Mobile card ───────────────────────────────────────────────── */}
      <a
        href={lotHref}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="schedule-row-lot-link"
        className={`flex items-center gap-2.5 rounded-cf-lg border border-cf-line bg-cf-surface px-3.5 py-3 lg:hidden ${STRIPE[auction.status]}`}
      >
        <LotPhoto src={auction.mainPhoto} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold">
            {date} · {time} · {auction.car_name}
          </div>
          <div className="truncate text-xs text-cf-ink-subtle">
            {auction.listing_id} · {bid} ·{' '}
            {t('auctionSchedule.board.columns.clientBids')} {bidCount}
          </div>
        </div>
        <span
          className={`shrink-0 rounded-cf-sm px-[7px] py-[3px] font-mono text-[9px] tracking-[0.08em] whitespace-nowrap uppercase ${BADGE[auction.status]}`}
        >
          {statusLabel}
        </span>
      </a>

      {/* ── Desktop row ───────────────────────────────────────────────── */}
      <div className="relative hidden lg:grid lg:min-w-[860px] lg:grid-cols-[120px_minmax(0,1fr)_100px_120px_130px_120px] lg:items-center lg:gap-4 lg:border-b lg:border-cf-line-soft lg:px-[18px] lg:py-3 lg:text-sm">
        <div>
          <div className="font-mono text-[13px]">{date}</div>
          <div className="text-xs text-cf-ink-subtle">{time}</div>
        </div>

        <div className="flex min-w-0 items-center gap-2.5">
          <span className={`h-2 w-2 shrink-0 rounded-cf-xs ${DOT[auction.status]}`} />
          <LotPhoto src={auction.mainPhoto} />
          {/* Stretched over the row: the whole desktop row opens the lot. */}
          <a
            href={lotHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="schedule-row-lot-link-desktop"
            className="truncate font-semibold after:absolute after:inset-0 after:content-[''] hover:underline focus-visible:outline-2 focus-visible:outline-cf-brand"
          >
            {auction.car_name}
          </a>
          <span className="shrink-0 text-xs text-cf-ink-subtle">{auction.listing_id}</span>
        </div>

        <span className="font-mono">{bid}</span>

        <span className={`font-mono ${bidCount > 0 ? 'text-cf-ok' : 'text-cf-ink-subtle'}`}>
          {bidCount > 0 ? bidCount : t('auctionSchedule.board.none')}
        </span>

        <span
          className={`inline-flex w-fit rounded-cf-sm px-2 py-1 font-mono text-[10px] tracking-[0.08em] uppercase ${BADGE[auction.status]}`}
        >
          {statusLabel}
        </span>

        <a
          href={href}
          className="relative z-[1] flex h-8 items-center justify-center justify-self-end rounded-cf-md border border-cf-line bg-cf-surface px-3 text-xs font-semibold hover:border-cf-ink"
        >
          {t('auctionSchedule.board.columns.edit')}
        </a>
      </div>
    </>
  );
}

const STRIPE: Record<AuctionScheduleItem['status'], string> = {
  active: 'shadow-[inset_3px_0_0_var(--color-cf-accent)]',
  ending: 'shadow-[inset_3px_0_0_var(--color-cf-danger)]',
  ended: 'shadow-[inset_3px_0_0_var(--color-cf-past)]',
};
